import React, { useEffect } from 'react';
import PropTypes from 'prop-types';

import { useContext } from '@hooks';
import { formatPrice, mergeClasses } from '@utils';

import { t } from 'ttag';
import { ProductCardContext } from './ProductCard';

/**
 * ProductCard.Price is a child component that displays the Price information within a ProductCard component.
 * Must be used as a child component of the ProductCard.
 *
 * @component ProductCard.Price
 * @param {Object} props - The properties for the ProductCard.Price component.
 * @param {string} [props.className] - Optional additional CSS classes to apply to the ProductCard.Price container.
 * @param {boolean} props.isDiscounted - Indicates whether the product has a discounted price.
 * @param {string} props.locale - The locale used for formatting the price.
 * @param {number} props.maxPrice - The maximum Price of the product.
 * @param {number} props.minPrice - The minimum Price of the product.
 * @param {string} props.priceCurrency - The currency used for the Price.
 * @param {number} props.recommendedRetailPrice - The Recommended Retail Price of the product.
 * @param {Object} restProps - The remaining properties to be passed to the component.
 * @returns {ReactElement} The rendered ProductCard.Price component.
 * @example
 * <ProductCard
 *   href="#"
 *   productId={266712}
 *   isExclusive={true}
 *   isLimited={false}
 *   isPreorder={false}
 * >
 *   <ProductCard.Price
 *     isDiscounted={true}
 *     locale="en-US"
 *     maxPrice={120}
 *     minPrice={100}
 *     priceCurrency="USD"
 *     recommendedRetailPrice={150}
 *   />
 *   <ProductCard.Image src="https://thumblr.uniid.it/product/266712/fdf6817198ad.jpg" alt="Nike Air Zoom Mercurial Superfly 9 Academy AG Dream Speed 6" />
 *   <ProductCard.Title>Nike Air Zoom Mercurial Superfly 9 Academy AG Dream Speed 6</ProductCard.Title>
 * </ProductCard>
 */
export default function ProductCardPrice({
  className,
  isDiscounted,
  locale,
  maxPrice,
  minPrice,
  priceCurrency,
  recommendedRetailPrice,
  ...restProps
}) {
  const { setDiscounted } = useContext(
    ProductCardContext,
    'ProductCard.Price must be a child of ProductCard.'
  );

  useEffect(() => {
    setDiscounted(isDiscounted);
  }, [isDiscounted, setDiscounted]);

  if (!minPrice || !maxPrice || !recommendedRetailPrice) {
    return null;
  }

  return (
    <span
      className={mergeClasses(
        'flex min-h-[16px] items-center gap-1 px-2 text-xs font-semibold text-black @md:min-h-[20px] @md:px-4 @md:text-sm',
        className
      )}
      {...restProps}
    >
      {minPrice !== maxPrice && t`From`}{' '}
      {formatPrice(minPrice, priceCurrency, locale)}{' '}
      {minPrice !== recommendedRetailPrice && (
        <span className="text-dark-gray line-through">
          {formatPrice(recommendedRetailPrice, priceCurrency, locale)}
        </span>
      )}
    </span>
  );
}

ProductCardPrice.propTypes = {
  className: PropTypes.string,
  isDiscounted: PropTypes.number.isRequired,
  locale: PropTypes.string.isRequired,
  maxPrice: PropTypes.number.isRequired,
  minPrice: PropTypes.number.isRequired,
  priceCurrency: PropTypes.string.isRequired,
  recommendedRetailPrice: PropTypes.number.isRequired
};

ProductCardPrice.defaultProps = {
  className: ''
};
